import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { Link } from 'react-router-dom';
import { User, Music, ShoppingBag, Coins, Calendar, Play, DollarSign, Download } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DashboardPage() {
  const { user } = useAuth();
  const { playTrack } = usePlayer();
  const [purchases, setPurchases] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [royalties, setRoyalties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [purchasesRes, tokensRes, royaltiesRes] = await Promise.all([
          axios.get(`${API_URL}/purchases`),
          axios.get(`${API_URL}/tokens/owned`),
          axios.get(`${API_URL}/royalties/user`)
        ]);
        setPurchases(purchasesRes.data);
        setTokens(tokensRes.data);
        setRoyalties(royaltiesRes.data);
      } catch (error) {
        logger.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const trackPurchases = purchases.filter(p => p.item_type === 'track');
  const albumPurchases = purchases.filter(p => p.item_type === 'album');
  const merchPurchases = purchases.filter(p => p.item_type === 'merch');

  const totalTokenValue = tokens.reduce((acc, t) => {
    return acc + (t.tokens_owned * (t.track?.token_price || 0));
  }, 0);

  const totalRoyaltiesEarned = royalties.reduce((acc, r) => acc + r.amount, 0);

  const handleDownload = async (trackId) => {
    try {
      const response = await axios.get(`${API_URL}/download/track/${trackId}`);
      const link = document.createElement('a');
      link.href = response.data.download_url;
      link.download = response.data.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      logger.error('Download failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="main-content pt-24" data-testid="dashboard-page">
      <div className="container py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{user?.name}</h1>
            <p className="text-gray-400">{user?.email}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 text-purple-400 mb-2">
              <Music className="w-5 h-5" />
              <span className="text-sm">Tracks</span>
            </div>
            <p className="text-2xl font-bold">{trackPurchases.length}</p>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 text-cyan-400 mb-2">
              <ShoppingBag className="w-5 h-5" />
              <span className="text-sm">Merch</span>
            </div>
            <p className="text-2xl font-bold">{merchPurchases.length}</p>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 text-pink-400 mb-2">
              <Coins className="w-5 h-5" />
              <span className="text-sm">Tokens Owned</span>
            </div>
            <p className="text-2xl font-bold">{tokens.reduce((acc, t) => acc + t.tokens_owned, 0)}%</p>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 text-yellow-400 mb-2">
              <Coins className="w-5 h-5" />
              <span className="text-sm">Token Value</span>
            </div>
            <p className="text-2xl font-bold token-data">${totalTokenValue.toFixed(2)}</p>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 text-green-400 mb-2">
              <DollarSign className="w-5 h-5" />
              <span className="text-sm">Royalties</span>
            </div>
            <p className="text-2xl font-bold text-green-400">${totalRoyaltiesEarned.toFixed(2)}</p>
          </div>
        </div>

        <Tabs defaultValue="tokens" className="w-full">
          <TabsList className="mb-8 bg-white/5 p-1 rounded-lg">
            <TabsTrigger 
              value="tokens"
              className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-white px-6 py-2 rounded-md"
              data-testid="tokens-tab"
            >
              My Tokens ({tokens.length})
            </TabsTrigger>
            <TabsTrigger 
              value="royalties"
              className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-white px-6 py-2 rounded-md"
              data-testid="royalties-tab"
            >
              Royalties ({royalties.length})
            </TabsTrigger>
            <TabsTrigger 
              value="purchases"
              className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-white px-6 py-2 rounded-md"
              data-testid="purchases-tab"
            >
              Purchases ({purchases.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tokens" className="animate-fade-in">
            {tokens.length === 0 ? (
              <div className="empty-state glass-card py-16">
                <Coins className="empty-state-icon w-16 h-16" />
                <h3 className="text-xl font-semibold mb-2">No tokens yet</h3>
                <p className="text-gray-400 mb-6">Start investing in music ownership</p>
                <Link to="/tokens" className="btn-primary" data-testid="browse-tokens">
                  Browse Token Marketplace
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tokens.map((ownership) => (
                  <div key={ownership.id} className="token-card glass-card p-5" data-testid={`owned-token-${ownership.track_id}`}>
                    <div className="flex gap-4 mb-4">
                      <img 
                        src={ownership.track?.cover_url}
                        alt={ownership.track?.title}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{ownership.track?.title}</h3>
                        <p className="text-sm text-gray-400">{ownership.track?.album}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-2xl font-bold gradient-text">{ownership.tokens_owned}%</p>
                        <p className="text-xs text-gray-500">Ownership</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-2xl font-bold token-data">
                          ${(ownership.tokens_owned * (ownership.track?.token_price || 0)).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">Value</p>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => playTrack(ownership.track)}
                      className="w-full mt-4 btn-ghost flex items-center justify-center gap-2 border border-white/10"
                      data-testid={`play-owned-${ownership.track_id}`}
                    >
                      <Play className="w-4 h-4" />
                      Play Track
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="royalties" className="animate-fade-in">
            {royalties.length === 0 ? (
              <div className="empty-state glass-card py-16">
                <DollarSign className="empty-state-icon w-16 h-16" />
                <h3 className="text-xl font-semibold mb-2">No royalties yet</h3>
                <p className="text-gray-400 mb-6">Own tokens to earn royalties when music generates revenue</p>
                <Link to="/tokens" className="btn-primary" data-testid="browse-tokens-royalties">
                  Browse Token Marketplace
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {royalties.map((royalty) => (
                  <div key={royalty.id || `${royalty.track_id}-${royalty.distributed_at}`} className="glass-card p-4 flex items-center gap-4" data-testid={`royalty-${royalty.id}`}>
                    <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{royalty.track_title}</h3>
                      <p className="text-sm text-gray-400">{royalty.tokens_owned}% ownership</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-400">+${royalty.amount.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                        <Calendar className="w-3 h-3" />
                        {new Date(royalty.distributed_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="purchases" className="animate-fade-in">
            {purchases.length === 0 ? (
              <div className="empty-state glass-card py-16">
                <ShoppingBag className="empty-state-icon w-16 h-16" />
                <h3 className="text-xl font-semibold mb-2">No purchases yet</h3>
                <p className="text-gray-400 mb-6">Start shopping to see your purchases here</p>
                <Link to="/music" className="btn-primary" data-testid="browse-music">
                  Browse Music
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {purchases.map((purchase) => (
                  <div 
                    key={purchase.id}
                    className="glass-card p-4 flex items-center gap-4"
                    data-testid={`purchase-${purchase.id}`}
                  >
                    <img 
                      src={purchase.item_details?.cover_url || purchase.item_details?.image_url}
                      alt={purchase.item_details?.title || purchase.item_details?.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">
                        {purchase.item_details?.title || purchase.item_details?.name}
                      </h3>
                      <p className="text-sm text-gray-400 capitalize">{purchase.item_type}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-purple-400">${purchase.price.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(purchase.purchased_at).toLocaleDateString()}
                      </p>
                    </div>
                    {purchase.item_type === 'track' && (
                      <>
                        <button
                          onClick={() => playTrack(purchase.item_details)}
                          className="player-btn bg-white/5"
                          data-testid={`play-purchase-${purchase.id}`}
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(purchase.item_id)}
                          className="player-btn bg-white/5 text-green-400"
                          data-testid={`download-purchase-${purchase.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
